import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuCheck } from 'react-icons/lu';
import { Channel } from '../../NewCommunication/ChannelStepContainer';
import { ChannelSendConfig, ChannelSendFlowConfig } from './ChannelSendFlowConfig';
import { useNewCommunicationValues } from '../../NewCommunication/context/useNewCommunicationValues';
import { useNewCommunicationActions } from '../../NewCommunication/context/useNewCommunicationActions';
import { WizardNav } from '../WizardNav';

export type ChannelConfigs = Partial<Record<Channel, ChannelSendConfig>>;

const CHANNEL_ORDER: Channel[] = ['whatsapp', 'sms', 'email'];

/** Sub-stepper for configuring the send flow of each selected channel; pills are freely navigable. */
export const ChannelFlowsStep: FC = () => {
  const { t } = useTranslation('comuni');

  const { channels, channelConfigs: configs, draftAudienceId: audienceId } = useNewCommunicationValues();
  const { handleChannelConfigured, handleChannelReset, handleChannelNext, goToPreviousStep } = useNewCommunicationActions();

  const ordered = useMemo(() => CHANNEL_ORDER.filter((channel) => channels.includes(channel)), [channels]);
  const allConfigured = ordered.length > 0 && ordered.every((channel) => !!configs[channel]);

  const [activeChannel, setActiveChannel] = useState<Channel | null>(null);

  useEffect(() => {
    if (ordered.length === 0) {
      setActiveChannel(null);
      return;
    }
    setActiveChannel((current) => {
      if (current && ordered.includes(current)) return current;
      return ordered.find((channel) => !configs[channel]) ?? null;
    });
  }, [ordered, configs]);

  const selectChannel = useCallback(
    (channel: Channel) => {
      if (configs[channel]) {
        handleChannelReset(channel);
      }
      setActiveChannel(channel);
    },
    [configs, handleChannelReset]
  );

  const onChannelConfigured = useCallback(
    (channel: Channel, config: ChannelSendConfig) => {
      handleChannelConfigured(channel, config);
      // Exclude the channel just completed; `configs` is still pre-update for that key.
      const remaining = ordered.filter((item) => item !== channel && !configs[item]);
      setActiveChannel(remaining[0] ?? null);
    },
    [configs, handleChannelConfigured, ordered]
  );

  // Show form while not all configured; fall back to first incomplete if active is briefly null.
  const formChannel = !allConfigured ? activeChannel ?? ordered.find((channel) => !configs[channel]) ?? null : null;

  return (
    <section className=' w-full grid grid-cols-1 rounded-2xl border p-6'>
      <div className='flex flex-wrap items-center gap-2.5'>
        {ordered.map((channel) => {
          const configured = !!configs[channel];
          const isActive = channel === (formChannel ?? activeChannel);
          return (
            <button
              key={channel}
              type='button'
              title={configured ? t('flows.configured') : undefined}
              onClick={() => selectChannel(channel)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] ${
                isActive
                  ? 'border-brand-600 bg-brand-50/40 text-brand-700'
                  : configured
                    ? 'bg-green-600 text-white-100 hover:border-brand-200'
                    : 'border-dashed text-quaternary-400 hover:border-brand-200 hover:text-brand-700'
              }`}
            >
              <span className='flex size-4 flex-none items-center justify-center rounded-full bg-white-100'>
                <LuCheck className='size-2.5 text-green-600' strokeWidth={3} />
              </span>
              {t(`channel.${channel}`)}
            </button>
          );
        })}
      </div>

      {formChannel ? (
        <div className='mt-6'>
          <h3 className='text-[19px] font-extrabold tracking-tight text-primary-900'>{t('flows.channelTitle', { channel: t(`channel.${formChannel}`) })}</h3>
          <div className='mt-4'>
            <ChannelSendFlowConfig
              key={formChannel}
              channel={formChannel}
              audienceId={audienceId}
              onConfigured={(config) => onChannelConfigured(formChannel, config)}
            />
          </div>
        </div>
      ) : (
        <div className='mt-6 space-y-2.5'>
          <p className='text-sm font-semibold text-secondary-700'>{t('flows.allConfigured')}</p>
          {ordered.map((channel) => (
            <div key={channel} className='flex items-center justify-between rounded-xl border bg-white-100 px-5 py-3.5'>
              <span className='flex items-center gap-2.5 text-[14.5px] font-bold text-primary-900'>
                <span className='flex size-5 flex-none items-center justify-center rounded-full bg-brand-700'>
                  <LuCheck className='size-3 text-white-100' strokeWidth={3} />
                </span>
                {t(`channel.${channel}`)}
              </span>
              <button type='button' onClick={() => selectChannel(channel)} className='text-[13px] font-bold text-brand-700 hover:text-brand-800'>
                {t('flows.edit')}
              </button>
            </div>
          ))}
        </div>
      )}
      <WizardNav
        primaryText={t('composer.next')}
        onPrimary={() => handleChannelNext()}
        primaryDisabled={!allConfigured}
        onBack={goToPreviousStep}
      />
    </section>
  );
};
